import { refreshSession } from '@/api/client';
import { customerTokenStore } from '@/api/tokenStore';
import * as accountApi from '@/features/storefront/account/api/account';
import {
  fetchStorefrontCareers,
  fetchStorefrontJobOpening,
  submitJobApplication,
} from '@/features/storefront/api/careers';
import { fetchStorefrontLocations } from '@/features/storefront/api/locations';
import { fetchStorefrontTeam, fetchStorefrontTeamMember } from '@/features/storefront/api/team';
import * as api from '@/features/storefront/api/storefront';
import type {
  StorefrontAccountSource,
  StorefrontDataSource,
} from '@/features/storefront/data/source';

/**
 * The merchant's own shop, read from the API.
 *
 * A thin pass-through on purpose. Every behaviour the storefront had before
 * the source abstraction existed still lives in `api/storefront.ts` - the
 * hostname resolution, the `auth: 'none'` rule, the zod parsing at the
 * boundary - and this only gives it a name the pages can ask for by. Nothing
 * about the live shop changed; it just stopped being the only answer.
 */
export function liveSourceFor(host?: string): StorefrontDataSource {
  return {
  // The host is part of the identity, so the dashboard's customiser reading
  // one merchant's shop cannot answer from the cache of another's.
  key: host ? `live:${host}` : 'live',
  preview: false,

  context: () => api.fetchContext(host),
  products: (query) => api.fetchStorefrontProducts({ ...query, host }),
  facets: (filters) => api.fetchStorefrontFacets(filters, host),
  // The four below resolve the shop from the browser's own hostname rather
  // than from `host`, because the API has no parameter for it. That is not a
  // gap: the only caller that passes a host is the dashboard customiser, and
  // a customiser does not open product pages or fill carts.
  product: (slug) => api.fetchStorefrontProduct(slug),
  relatedProducts: (slug, limit) => api.fetchStorefrontRelatedProducts(slug, limit),
  productBundle: (slug) => api.fetchStorefrontProductBundle(slug),
  categories: () => api.fetchStorefrontCategories(host),

  createCart: () => api.createCart(),
  fetchCart: (token) => api.fetchCart(token),
  setCartItem: (token, variantId, quantity) => api.setCartItem(token, variantId, quantity),
  applyDiscount: (token, code) => api.applyDiscount(token, code),
  applyGiftCard: (token, code) => api.applyGiftCard(token, code),
  shippingOptions: (request) => api.fetchShippingOptions(request),
  setCartShipping: (token, update) => api.setCartShipping(token, update),
  setCartNote: (token, note) => api.setCartNote(token, note),

  checkout: (payload) => api.checkout(payload),
  // No public endpoint hands over an order by its number, and there should not
  // be one: order numbers are short and sequential, so an endpoint that took
  // one would let anybody walk the shop's order book. The confirmation page
  // renders what checkout returned to it instead.
  order: () => Promise.resolve(null),

  pages: () => api.fetchStorefrontPages(host),
  page: (handle) => api.fetchStorefrontPage(handle, host),
  blogs: () => api.fetchStorefrontBlogs(host),
  blog: (handle) => api.fetchStorefrontBlog(handle, host),
  posts: (blogHandle, query) => api.fetchStorefrontPosts(blogHandle, { ...query, host }),
  post: (blogHandle, postHandle) => api.fetchStorefrontPost(blogHandle, postHandle, host),

  contact: (message) => api.sendContactMessage(message),
  subscribe: (email, name) => api.subscribeToNewsletter(email, name),

  // Ids only. The section and the service arrive with the request because a
  // preview has to compute the grid itself, but on a real shop the diary is
  // the server's and asking it anything else would be asking it to trust a
  // browser about when the business opens.
  bookingAvailability: ({ section, service, day, teamMemberId }) =>
    api.fetchBookingAvailability({
      sectionId: section.id,
      serviceId: service.id,
      day,
      teamMemberId,
    }),
  createBooking: ({
    section,
    service,
    startAt,
    sessionStartsAt,
    name,
    email,
    phone,
    notes,
    teamMemberId,
    customerAddress,
    answers,
    whatsappOptIn,
    payNow,
    usePackage,
  }) =>
    api.createBooking({
      section_id: section.id,
      service_id: service.id,
      // A slot or a token - never both, so a token request carries no time.
      ...(sessionStartsAt ? { session_starts_at: sessionStartsAt } : { start_at: startAt }),
      name,
      ...(email ? { email } : {}),
      phone,
      notes,
      // Only sent when there is something to say, so a section that does not
      // book by person posts exactly the body it always has.
      ...(teamMemberId ? { team_member_id: teamMemberId } : {}),
      ...(customerAddress ? { customer_address: customerAddress } : {}),
      // Likewise the merchant's questions: a section that asks none posts no
      // `answers` key at all.
      ...(answers && Object.keys(answers).length > 0 ? { answers } : {}),
      // A WhatsApp opt-in means nothing without the number it is for.
      ...(whatsappOptIn && phone ? { whatsapp_opt_in: true } : {}),
      // Payment answers only where the shopper was asked, so a shop without
      // booking payments posts exactly the body it always has.
      ...(payNow !== undefined ? { pay_now: payNow } : {}),
      ...(usePackage ? { use_package: true } : {}),
    }),

  startBookingPayment: (bookingId, token) => api.startBookingPayment(bookingId, token),
  verifyBookingPayment: (bookingId, token, providerPaymentId) =>
    api.verifyBookingPayment(bookingId, token, providerPaymentId),
  bookingStatus: (bookingId, token) => api.getBooking(bookingId, token),
  packages: () => api.listPackages(host),

  // Resolved from the browser's hostname, like the product itself: the
  // customiser never asks for reviews - it draws stand-ins - so no host is
  // needed here.
  productReviews: (slug, query) => api.fetchProductReviews(slug, query),
  myProductReview: (slug) => api.fetchMyProductReview(slug),
  submitProductReview: (slug, draft) => api.submitProductReview(slug, draft),
  updateMyProductReview: (slug, draft) => api.updateMyProductReview(slug, draft),
  uploadReviewPhoto: async (slug, file) => (await api.uploadReviewPhoto(slug, file)).url,

  // Questions and back-in-stock alerts, like reviews: by the browser's host.
  productQuestions: (slug, query) => api.fetchProductQuestions(slug, query),
  askProductQuestion: async (slug, draft) => {
    await api.askProductQuestion(slug, draft);
  },
  subscribeStockAlert: async (variantId, email, pushSubscription) => {
    await api.subscribeStockAlert(variantId, email, pushSubscription);
  },

  // By `host` when given, so the customiser shows the merchant's own branches.
  locations: () => fetchStorefrontLocations(host),

  // By `host` too, so the customiser's team section shows the merchant's own
  // people and its fixed-person booking preview names the right one.
  team: () => fetchStorefrontTeam(host),
  teamMember: (slug) => fetchStorefrontTeamMember(slug, host),

  // By `host` too, so the customiser's careers section lists the merchant's
  // own openings.
  careers: () => fetchStorefrontCareers(host),
  jobOpening: (slug) => fetchStorefrontJobOpening(slug, host),
  applyToJob: (slug, form) => submitJobApplication(slug, form, host),

  publicQueue: (teamMember) => api.fetchPublicQueue(teamMember, host),
  };
}

/** The shop this browser is on. */
export const LIVE_SOURCE: StorefrontDataSource = liveSourceFor();

/**
 * The signed-in shopper's own data, from the account API.
 *
 * The session methods own the token store, which is what keeps the auth
 * provider free of it: on the live shop a sign-in has a credential to keep and
 * a refresh cookie to try on boot, and in a preview neither exists. Putting
 * that here rather than in the provider is the difference between one provider
 * and two.
 */
export const LIVE_ACCOUNT_SOURCE: StorefrontAccountSource = {
  resume: async () => {
    // On boot the access token is gone - it only ever lived in memory - but
    // the refresh cookie may still be good, so try once before deciding the
    // visitor is just a browser.
    const token = customerTokenStore.get() ?? (await refreshSession('customer'));
    if (!token) return null;
    try {
      return await accountApi.fetchCustomerProfile();
    } catch {
      return null;
    }
  },

  signIn: async (email, password, captchaToken) => {
    const session = await accountApi.loginCustomer(email, password, captchaToken);
    customerTokenStore.set(session.access_token);
    return session.customer;
  },

  signOut: async () => {
    try {
      await accountApi.logoutCustomer();
    } finally {
      // Whatever the server said, this browser is done with the session: a
      // failed logout call must not leave a signed-in-looking header behind.
      customerTokenStore.clear();
    }
  },

  register: (payload) => accountApi.registerCustomer(payload),
  profile: () => accountApi.fetchCustomerProfile(),
  updateProfile: (payload) => accountApi.updateCustomerProfile(payload),
  changePassword: async (currentPassword, newPassword) => {
    await accountApi.changeCustomerPassword(currentPassword, newPassword);
  },

  verifyEmail: async (token) => {
    await accountApi.verifyCustomerEmail(token);
  },
  resendVerification: async (email) => {
    await accountApi.resendCustomerVerification(email);
  },
  requestPasswordReset: async (email) => {
    await accountApi.requestCustomerPasswordReset(email);
  },
  resetPassword: async (token, password) => {
    await accountApi.resetCustomerPassword(token, password);
  },

  sessions: () => accountApi.fetchCustomerSessions(),
  revokeSession: (sessionId) => accountApi.revokeCustomerSession(sessionId),
  revokeOtherSessions: () => accountApi.revokeOtherCustomerSessions(),

  orders: (page) => accountApi.fetchCustomerOrders(page),
  orderDetail: (orderId) => accountApi.fetchCustomerOrder(orderId),
  invoice: (orderId) => accountApi.fetchCustomerInvoice(orderId),
  cancelOrder: (orderId, reason, comment) =>
    accountApi.cancelCustomerOrder(orderId, reason, comment),
  requestOnOrder: (orderId, payload) => accountApi.createOrderRequest(orderId, payload),
  uploadRequestPhoto: async (orderId, file) =>
    (await accountApi.uploadOrderRequestPhoto(orderId, file)).url,
  reorder: (orderId, token) => accountApi.reorder(orderId, token),

  appointments: (range, page) => accountApi.fetchCustomerAppointments(range, page),
  appointment: (appointmentId) => accountApi.fetchCustomerAppointment(appointmentId),
  cancelAppointment: (appointmentId) => accountApi.cancelCustomerAppointment(appointmentId),

  packages: () => api.listMyPackages(),
  buyPackage: (packageId) => api.buyPackage(packageId),
  verifyPackagePurchase: (purchaseId, providerPaymentId) =>
    api.verifyPackagePurchase(purchaseId, providerPaymentId),

  addresses: () => accountApi.fetchCustomerAddresses(),
  createAddress: (payload) => accountApi.createCustomerAddress(payload),
  updateAddress: (addressId, payload) => accountApi.updateCustomerAddress(addressId, payload),
  setDefaultAddress: (addressId) => accountApi.setDefaultCustomerAddress(addressId),
  deleteAddress: async (addressId) => {
    await accountApi.deleteCustomerAddress(addressId);
  },

  buyAgain: (limit) => accountApi.fetchBuyAgain(limit),
  recommendations: (limit) => accountApi.fetchAccountRecommendations(limit),
};
